import { identify, init, track } from '@chamaeleonidae/chmln';
import { useUser } from 'modules/User';
import { useCallback, useContext } from 'react';

import ChameleonContext from './chameleonContext';

type ChameleonProviderProps = {
  children: React.ReactNode;
};

declare global {
  interface Window {
    chmln?: Chameleon_.ChameleonCommandSignatures | undefined;
  }
}

export const ChameleonProvider = ({ children }: ChameleonProviderProps) => {
  const initChameleon = useCallback(() => {
    try {
      init(process.env.REACT_APP_CHAMELEON_KEY!);
    } catch (e) {
      if (e instanceof Error) {
        console.error("Couldn't initialize Chameleon", e.message);
      }
    }
  }, []);

  const identifyChameleon = useCallback((id: string, props: object) => {
    try {
      if (!!window.chmln) {
        identify(id, props);
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error("Couldn't identify Chameleon", e.message);
      }
    }
  }, []);

  const trackChameleon = useCallback((event: string) => {
    try {
      track(event);
    } catch (e) {
      if (e instanceof Error) {
        console.error("Couldn't track Chameleon", e.message);
      }
    }
  }, []);

  const providerValues = {
    initChameleon,
    identifyChameleon,
    trackChameleon,
  };

  if (!window.chmln) {
    initChameleon();
  }

  const { data: userInfo } = useUser();
  if (!!window.chmln && userInfo) {
    identifyChameleon(userInfo.user_id, {
      email: userInfo.email,
      permission: userInfo.user_type,
      date_joined: new Date(userInfo.created_at).getTime(),
    });
    trackChameleon('Load Editor2');
  }
  return <ChameleonContext.Provider value={providerValues}>{children}</ChameleonContext.Provider>;
};

export const useChameleon = () => {
  return useContext(ChameleonContext) ?? { trackChameleon: () => {} };
};
